import { UserReviews_userProfile$data } from '#app/__generated__/relay/UserReviews_userProfile.graphql.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useIsLargeScreen } from '#app/hooks/useIsLargeScreen.js';
import { formatName } from '#app/utilities/formatName.js';
import * as styles from './UserReviewCard.styles.js';
import { UserReviewText } from './UserReviewText.js';
import { ChevronDownIcon } from '@contra/icons/icons';
import { useCallback, useState } from 'react';

type Props = {
  readonly authorRoleOnProject: string;
  readonly reviews: Array<
    Pick<
      UserReviews_userProfile$data['userReviews']['edges'][0]['node'],
      'associatedWork' | 'author' | 'createdAt' | 'reviewText'
    >
  >;
};

export const MoreReviews = ({ authorRoleOnProject, reviews }: Props) => {
  const [review, ...otherReviews] = reviews;
  const otherReviewsCount = otherReviews.length;
  const hasMoreReviews = otherReviewsCount > 0;
  const [showOtherReviews, setShowOtherReviews] = useState(false);
  const otherReviewsTextSuffix = otherReviewsCount > 1 ? 's' : '';
  const otherReviewsText = `${otherReviewsCount} other review${otherReviewsTextSuffix} from ${review.author.firstName}`;
  const authorFullName = formatName(
    { firstName: review.author.firstName, lastName: review.author.lastName },
    'fullName',
  );

  const otherReviewsView = otherReviews.map((otherReview) => (
    <UserReviewText
      authorRoleOnProject={authorRoleOnProject}
      createdAt={otherReview.createdAt}
      key={otherReview.createdAt}
      reviewText={otherReview.reviewText}
      showDate
    />
  ));
  const allReviewsView = (
    <styles.MoreReviews
      $extraMarginOnTop
      $isVisible
    >
      {reviews.map((listReview) => (
        <UserReviewText
          authorRoleOnProject={authorRoleOnProject}
          createdAt={listReview.createdAt}
          key={listReview.createdAt}
          reviewText={listReview.reviewText}
          showDate
        />
      ))}
    </styles.MoreReviews>
  );

  const isLargeScreen = !useIsLargeScreen();

  const { openModal } = useModal({
    identifier: 'MoreReviewsModal',
    modalProps: {
      ariaLabel: 'More Reviews',
      content: allReviewsView,
      heading: `All reviews from ${authorFullName}`,
    },
  });

  const onShowOtherReviews = useCallback(() => {
    if (isLargeScreen) {
      setShowOtherReviews(!showOtherReviews);
    } else {
      openModal();
    }
  }, [isLargeScreen, openModal, showOtherReviews]);

  if (!hasMoreReviews) {
    return null;
  }

  return (
    <styles.MoreReviewsContainer>
      <styles.MoreReviewsCtaContainer $isOpen={showOtherReviews}>
        <styles.MoreReviewsCta onClick={onShowOtherReviews}>
          {otherReviewsText}
        </styles.MoreReviewsCta>
        <ChevronDownIcon size={16} />
      </styles.MoreReviewsCtaContainer>

      <styles.MoreReviews $isVisible={showOtherReviews}>
        {otherReviewsView}
      </styles.MoreReviews>
    </styles.MoreReviewsContainer>
  );
};
